import { Injectable, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

export interface DeviceDetails {
  platform: 'ios' | 'android' | 'web';
  model: string;
  manufacturer: string;
  osVersion: string;
  appVersion: string;
  appBuild: string;
  deviceId: string;
  isVirtual: boolean;
}

@Injectable({ providedIn: 'root' })
export class PlatformService {
  readonly isNativeMobile = signal(false);

  readonly platform = signal<'ios' | 'android' | 'web'>('web');

  readonly deviceDetails = signal<DeviceDetails | null>(null);

  private deviceDetailsPromise: Promise<void> | null = null;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    const isNative = Capacitor.isNativePlatform();
    const currentPlatform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

    this.isNativeMobile.set(isNative);
    this.platform.set(currentPlatform);

    if (isNative) {
      this.deviceDetailsPromise = this.loadDeviceDetails();
    }
  }

  async ensureDeviceDetails(): Promise<DeviceDetails | null> {
    if (this.deviceDetails()) {
      return this.deviceDetails();
    }
    if (this.deviceDetailsPromise) {
      await this.deviceDetailsPromise;
    }
    return this.deviceDetails();
  }

  private async loadDeviceDetails(): Promise<void> {
    try {
      const [deviceInfo, deviceId, appInfo] = await Promise.all([Device.getInfo(), Device.getId(), App.getInfo()]);

      this.deviceDetails.set({
        platform: this.platform(),
        model: deviceInfo.model,
        manufacturer: deviceInfo.manufacturer,
        osVersion: deviceInfo.osVersion,
        appVersion: appInfo.version,
        appBuild: appInfo.build,
        deviceId: deviceId.identifier,
        isVirtual: deviceInfo.isVirtual,
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- Cannot use LoggingService here due to circular dependency (LoggingService depends on PlatformService)
      console.warn('PlatformService: Failed to load device details', error);
    }
  }

  isIOS(): boolean {
    return Capacitor.getPlatform() === 'ios';
  }

  isAndroid(): boolean {
    return Capacitor.getPlatform() === 'android';
  }

  isWeb(): boolean {
    return Capacitor.getPlatform() === 'web';
  }

  getDeviceInfoString(): string {
    const details = this.deviceDetails();
    if (!details) {
      return 'Web Browser';
    }

    return `${details.platform.toUpperCase()} ${details.osVersion} | ${details.manufacturer} ${details.model} | App v${
      details.appVersion
    } (${details.appBuild})`;
  }

  getDeviceContext(): Record<string, string | boolean> {
    const details = this.deviceDetails();
    if (!details) {
      return {
        platform: 'web',
        userAgent: navigator.userAgent,
      };
    }

    return {
      platform: details.platform,
      model: details.model,
      manufacturer: details.manufacturer,
      osVersion: details.osVersion,
      appVersion: details.appVersion,
      appBuild: details.appBuild,
      isVirtual: details.isVirtual,
    };
  }
}
